<template>
  <el-dialog
    :visible.sync="innerVisible"
    width="920px"
    top="7vh"
    :custom-class="dialogCustomClass"
    :close-on-click-modal="false"
    :lock-scroll="true"
    :append-to-body="true"
    @close="handleClose"
    @opened="handleDialogOpened"
  >
    <template slot="title">
      <div class="batch-tag-dialog__title">
        <span class="batch-tag-dialog__titleText">{{ dialogTitleText }}</span>
      </div>
    </template>

    <div class="batch-tag-dialog__body">
      <!-- 未提交的 Excel 打标草稿：选资源页整宽展示；带左栏的页移入主内容区 -->
      <div
        v-if="showDraftBanner && wizardStep === wizardSteps.SCOPE"
        class="batch-tag-dialog__draftBanner"
      >
        <span class="batch-tag-dialog__draftBannerText">
          检测到未提交的 Excel 打标草稿（{{ storedDraft.resourceCount }} 条，导出于 {{ draftExportedLabel }}）
        </span>
        <span class="batch-tag-dialog__draftBannerActions">
          <el-button type="text" size="small" @click="handleRestoreDraft">恢复草稿</el-button>
          <el-button type="text" size="small" @click="handleDiscardDraft">丢弃</el-button>
        </span>
      </div>

      <!-- 说明显示屏：选资源页整宽；带左栏的页移入主内容区 -->
      <div
        v-if="showHelpCarousel && wizardStep === wizardSteps.SCOPE"
        class="batch-tag-dialog__helpDisplay"
      >
        <div
          v-if="isScopeStep"
          class="batch-tag-dialog__helpBar scrollControlBar__broadcastNote batch-tag-dialog__helpBar--scope"
        >
          <span class="batch-tag-dialog__scopeLead">{{ pickerScopeLabel }}</span>
          <span class="scrollControlBar__broadcastNoteSep" aria-hidden="true">·</span>
          <div class="batch-tag-dialog__helpCarousel scrollControlBar__summaryScope scrollControlBar__summaryScope--interactive">
            <control-bar-carousel
              :slides="helpCarouselSlides"
              :pause-on-hover="helpCarouselPauseOnHover"
            />
          </div>
        </div>
        <div
          v-else
          class="scrollControlBar__summaryScope scrollControlBar__summaryScope--interactive"
        >
          <control-bar-carousel
            :slides="helpCarouselSlides"
            :pause-on-hover="helpCarouselPauseOnHover"
          />
        </div>
      </div>

      <!-- 第 1 层：选资源 -->
      <resource-picker-panel
        v-if="wizardStep === wizardSteps.SCOPE"
        ref="resourcePicker"
        class="batch-tag-dialog__resourcePage"
        :value="pageSelection"
        :filters="frozenFilters"
        :filter-summary="filterSummary"
        :has-filters="hasFilters"
        :type-options="typeOptions"
        :list-profile="listProfile"
        @input="handleSelectionUpdate"
        @scope-meta-change="handlePickerScopeMetaChange"
      />

      <!-- 第 2 层：选打标方式（全高左栏 + 右侧内容区） -->
      <div
        v-else-if="wizardStep === wizardSteps.METHOD"
        class="batch-tag-dialog__wizardStage"
      >
        <aside class="batch-tag-dialog__wizardSidebar">
          <wizard-back-rail
            :aria-label="wizardBackAriaLabel"
            :steps="wizardSidebarSteps"
            @back="handleWizardBack"
          />
        </aside>
        <div class="batch-tag-dialog__wizardMain">
          <div
            v-if="showDraftBanner"
            class="batch-tag-dialog__draftBanner batch-tag-dialog__draftBanner--inWizard"
          >
            <span class="batch-tag-dialog__draftBannerText">
              检测到未提交的 Excel 打标草稿（{{ storedDraft.resourceCount }} 条，导出于 {{ draftExportedLabel }}）
            </span>
            <span class="batch-tag-dialog__draftBannerActions">
              <el-button type="text" size="small" @click="handleRestoreDraft">恢复草稿</el-button>
              <el-button type="text" size="small" @click="handleDiscardDraft">丢弃</el-button>
            </span>
          </div>
          <method-picker-panel
            class="batch-tag-dialog__methodPage"
            :value="tagMethod"
            :selection-count="pageSelection.length"
            @select="handleMethodSelect"
          />
        </div>
      </div>

      <!-- 第 3 层：页面 / Excel 打标（左栏返回 + 右侧配置区） -->
      <div
        v-else-if="isTagStep"
        class="batch-tag-dialog__wizardStage"
      >
        <aside class="batch-tag-dialog__wizardSidebar">
          <wizard-back-rail
            :aria-label="wizardBackAriaLabel"
            :steps="wizardSidebarSteps"
            @back="handleWizardBack"
          />
        </aside>
        <div class="batch-tag-dialog__wizardMain">
          <div
            v-if="showDraftBanner"
            class="batch-tag-dialog__draftBanner batch-tag-dialog__draftBanner--inWizard"
          >
            <span class="batch-tag-dialog__draftBannerText">
              检测到未提交的 Excel 打标草稿（{{ storedDraft.resourceCount }} 条，导出于 {{ draftExportedLabel }}）
            </span>
            <span class="batch-tag-dialog__draftBannerActions">
              <el-button type="text" size="small" @click="handleRestoreDraft">恢复草稿</el-button>
              <el-button type="text" size="small" @click="handleDiscardDraft">丢弃</el-button>
            </span>
          </div>
          <div class="batch-tag-dialog__helpDisplay batch-tag-dialog__helpDisplay--inWizard">
            <div class="scrollControlBar__summaryScope scrollControlBar__summaryScope--interactive">
              <control-bar-carousel
                :slides="helpCarouselSlides"
                :pause-on-hover="helpCarouselPauseOnHover"
              />
            </div>
          </div>
          <div class="batch-tag-dialog__tagWorkspace">
            <page-tag-tab
              v-if="wizardStep === wizardSteps.PAGE_TAG"
              ref="pageTab"
              class="batch-tag-dialog__tagConfig"
              :selection="pageSelection"
              v-model="pageState"
            />

            <excel-tag-tab
              v-else
              ref="excelTab"
              class="batch-tag-dialog__tagConfig"
              :selection="pageSelection"
              :filters="frozenFilters"
              :resource-type="resourceType"
              :filter-summary="filterSummary"
              :has-filters="hasFilters"
              :scope-ready="scopeReady"
              :direct-upload-mode="excelDirectEntry"
              :preview-rows="previewRows"
              :clear-warnings="clearWarnings"
              :preview-loading="previewLoading"
              :preview-empty-hint="previewEmptyHint"
              v-model="excelState"
              @parsed="handleExcelParsed"
              @scope-established="handleExcelScopeEstablished"
            />

            <!-- 页面打标：预览 bottom sheet；Excel 打标在 Tab 内嵌预览 -->
            <transition name="batch-tag-preview-sheet">
              <div
                v-if="previewOverlayOpen && wizardStep === wizardSteps.PAGE_TAG"
                class="batch-tag-dialog__previewLayer"
                role="dialog"
                aria-modal="true"
                aria-label="变更预览"
              >
                <div
                  class="batch-tag-dialog__previewBackdrop"
                  aria-hidden="true"
                  @click="closePreviewOverlay"
                  @wheel.prevent
                />
                <div class="batch-tag-dialog__previewPanel">
                  <div class="batch-tag-dialog__previewPanelHead">
                    <span class="batch-tag-dialog__previewPanelTitle">
                      变更预览
                      <span v-if="previewRows.length" class="batch-tag-dialog__previewPanelMeta">
                        · 共 {{ previewRows.length }} 条
                      </span>
                    </span>
                    <button
                      type="button"
                      class="batch-tag-dialog__previewCloseBtn"
                      @click="closePreviewOverlay"
                    >
                      收起
                    </button>
                  </div>
                  <div class="batch-tag-dialog__previewPanelBody">
                    <tag-preview-panel
                      :rows="previewRows"
                      :clear-warnings="clearWarnings"
                      :loading="previewLoading && !previewRows.length"
                      :empty-hint="previewEmptyHint"
                      overlay
                    />
                  </div>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </div>

    <span slot="footer" class="batch-tag-dialog__footer">
      <div class="batch-tag-dialog__footerLeading">
        <direct-excel-upload-entry
          v-if="wizardStep === wizardSteps.SCOPE"
          compact
          @select="enterDirectExcelUpload"
        />
        <button
          v-else-if="wizardStep === wizardSteps.PAGE_TAG"
          type="button"
          class="batch-tag-dialog__previewDockBtn"
          @click="togglePreviewOverlay"
        >
          <span class="batch-tag-dialog__previewDockLabel">
            <i class="el-icon-view" aria-hidden="true" />
            <span>变更预览</span>
          </span>
          <span class="batch-tag-dialog__previewDockMeta">{{ previewDockSummary }}</span>
          <span v-if="clearWarnings.length" class="batch-tag-dialog__previewDockWarn">
            · {{ clearWarnings.length }} 条将清空
          </span>
          <span class="batch-tag-dialog__previewDockAction">
            {{ previewOverlayOpen ? '收起' : '展开' }}
          </span>
        </button>
      </div>
      <span class="batch-tag-dialog__footerSpacer" />
      <div class="batch-tag-dialog__footerNav">
        <el-button
          v-if="wizardStep === wizardSteps.SCOPE"
          type="primary"
          size="small"
          class="batch-tag-dialog__primaryBtn"
          :disabled="!pageSelection.length"
          @click="handleScopeNext"
        >
          下一步<span v-if="pageSelection.length">（{{ pageSelection.length }}）</span>
        </el-button>
        <el-button
          v-else-if="isTagStep"
          type="primary"
          size="small"
          class="batch-tag-dialog__primaryBtn"
          :loading="submitting"
          :disabled="submitting || !canSubmit"
          @click="handleConfirmSubmit"
        >
          确认打标
        </el-button>
      </div>
    </span>
  </el-dialog>
</template>

<script>
import WizardBackRail from './batchTag/WizardBackRail.vue'
import DirectExcelUploadEntry from './batchTag/DirectExcelUploadEntry.vue'
import ResourcePickerPanel from './batchTag/ResourcePickerPanel.vue'
import MethodPickerPanel from './batchTag/MethodPickerPanel.vue'
import PageTagTab from './batchTag/PageTagTab.vue'
import ExcelTagTab from './batchTag/ExcelTagTab.vue'
import TagPreviewPanel from './batchTag/TagPreviewPanel.vue'
import ControlBarCarousel from './ControlBarCarousel.vue'
import {
  buildWizardHelpCarouselSlides,
  buildPickerScopeLabel,
  BATCH_TAG_HELP_CAROUSEL,
  WIZARD_PAGE_TITLES,
} from './batchTag/helpCopy'
import { buildFilterSummary, hasActiveFilters } from './batchTag/scopeHelper'
import { buildPreviewRows, collectClearWarnings } from './batchTag/previewBuilder'
import {
  TAG_BIND_MODE,
  GENERIC_RESOURCE_TYPE,
  BATCH_TAG_WIZARD_STEP,
} from './batchTag/constants'
import { batchBindResource, resourceTagsByKeys } from '../../../api/tagManage'
import {
  readBatchTagDraft,
  writeBatchTagDraft,
  clearBatchTagDraft,
  formatDraftExportedLabel,
} from './batchTag/batchTagDraft'

export default {
  name: 'BatchTagDialog',
  components: {
    WizardBackRail,
    DirectExcelUploadEntry,
    ResourcePickerPanel,
    MethodPickerPanel,
    PageTagTab,
    ExcelTagTab,
    TagPreviewPanel,
    ControlBarCarousel,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    /** 列表页勾选，打开弹窗时拷贝为 pageSelection 初始值 */
    selection: {
      type: Array,
      default: () => [],
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
    resourceType: {
      type: Number,
      default: GENERIC_RESOURCE_TYPE,
    },
    /** generic | aiAssistant：决定资源挑选面板走哪套列表 API */
    listProfile: {
      type: String,
      default: 'generic',
    },
    categoryOptions: {
      type: Array,
      default: () => [],
    },
    dataOptions: {
      type: Array,
      default: () => [],
    },
    statusOptions: {
      type: Array,
      default: () => [],
    },
    typeOptions: {
      type: Array,
      default: () => [],
    },
    defaultTab: {
      type: String,
      default: 'page',
    },
  },
  data() {
    return {
      wizardSteps: BATCH_TAG_WIZARD_STEP,
      /** 层进向导当前步骤 */
      wizardStep: BATCH_TAG_WIZARD_STEP.SCOPE,
      /** 第 2 层选定的方式：page | excel */
      tagMethod: 'page',
      /** 弹窗内草稿勾选，与列表勾选共用同一数据结构 */
      pageSelection: [],
      /** 打开时冻结的主列表筛选，供资源页继承查询范围 */
      frozenFilters: {},
      pageState: {
        selectedTags: [],
        mode: TAG_BIND_MODE.APPEND,
      },
      excelState: {
        mode: '',
        parsedRows: [],
        scopeKeySet: [],
      },
      previewRows: [],
      clearWarnings: [],
      previewLoading: false,
      submitting: false,
      existingTagMap: {},
      previewRefreshTimer: null,
      /** 变更预览悬浮层；收起时不占配置区高度 */
      previewOverlayOpen: false,
      /** 选择资源页列表总数，供顶栏范围轮播首帧 */
      pickerListTotalCount: 0,
      /** 跳过第 1 步：Excel 打标由上传文件自证 key 范围 */
      excelDirectEntry: false,
      /** sessionStorage 中的 Excel 草稿快照，用于顶栏恢复 banner */
      storedDraft: null,
      /** 当前会话已恢复或新建草稿后隐藏 banner */
      draftBannerDismissed: false,
    }
  },
  computed: {
    innerVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      },
    },
    dialogCustomClass() {
      const base = 'batch-tag-dialog'
      if (this.wizardStep === this.wizardSteps.SCOPE) {
        return `${base} batch-tag-dialog--scope`
      }
      if (this.wizardStep === this.wizardSteps.METHOD) {
        return `${base} batch-tag-dialog--method`
      }
      return `${base} batch-tag-dialog--tag`
    },
    filterSummary() {
      return buildFilterSummary(this.frozenFilters, {
        categoryOptions: this.categoryOptions,
        dataOptions: this.dataOptions,
        statusOptions: this.statusOptions,
      })
    },
    hasFilters() {
      return hasActiveFilters(this.frozenFilters)
    },
    /** Excel 步是否已具备作用范围：勾选、直接上传入口、或草稿/导出 scopeKeySet */
    scopeReady() {
      if (this.pageSelection.length > 0) {
        return true
      }
      if (this.excelDirectEntry) {
        return true
      }
      return (this.excelState.scopeKeySet || []).length > 0
    },
    showDraftBanner() {
      if (!this.storedDraft || this.draftBannerDismissed) {
        return false
      }
      if (this.wizardStep === this.wizardSteps.EXCEL_TAG && this.excelState.scopeKeySet.length) {
        return false
      }
      return true
    },
    draftExportedLabel() {
      return formatDraftExportedLabel(this.storedDraft && this.storedDraft.exportedAt)
    },
    isTagStep() {
      return (
        this.wizardStep === this.wizardSteps.PAGE_TAG ||
        this.wizardStep === this.wizardSteps.EXCEL_TAG
      )
    },
    /** 页面打标进入后即在后台刷新；展示由 overlay 控制 */
    previewDockSummary() {
      if (this.previewLoading) {
        return '更新中…'
      }
      if (this.wizardStep === this.wizardSteps.EXCEL_TAG && !(this.excelState.parsedRows || []).length) {
        return '上传 Excel 后查看'
      }
      if (!this.previewRows.length) {
        return '暂无数据'
      }
      const changeCount = this.previewRows.filter((row) => row.hasChange || row.willClear).length
      if (!changeCount) {
        return `${this.previewRows.length} 条 · 均无变化`
      }
      return `${changeCount} 条有变更`
    },
    canSubmit() {
      return !this.submitBlockReason
    },
    /**
     * 提交阻断原因：用于内部判断。预览未就绪不再阻断提交（预览仅辅助确认），
     * 避免按钮 disabled 时点击无反馈。
     */
    submitBlockReason() {
      if (!this.isTagStep) {
        return '请先完成打标配置'
      }
      if (this.previewLoading) {
        return '变更预览加载中，请稍候'
      }
      if (!this.isTagPanelReady) {
        return '打标面板初始化中，请稍候'
      }
      const context = this.getActiveContext()
      if (context.error) {
        return context.error
      }
      if (!context.items.length) {
        return '没有可提交的资源'
      }
      return ''
    },
    /**
     * 第 3 层子面板是否就绪。
     * Med: 旧实现用 !!this.$refs.pageTab / $refs.excelTab 判断,
     * 但 Vue 2 的 $refs 不在响应式系统里,首次求值时子组件 v-if 切换尚未 mount,
     * computed 会把 false 缓存下来;之后子组件 mount 完也不会重算,
     * 导致 submitBlockReason 永久命中「打标面板初始化中,请稍候」,
     * 「确认打标」按钮永久 disabled。这里只查响应式的 wizardStep,
     * 万一极快操作下 $refs 还没填充,getActiveContext() 里有 if (!tab) 兜底。
     */
    isTagPanelReady() {
      return this.wizardStep === this.wizardSteps.PAGE_TAG
        || this.wizardStep === this.wizardSteps.EXCEL_TAG
    },
    /** 弹窗标题：子页展示「批量打标 · 页面名」层级；方式选择页仅「批量打标」 */
    dialogTitleText() {
      if (this.wizardStep === this.wizardSteps.METHOD) {
        return '批量打标'
      }
      const pageName = WIZARD_PAGE_TITLES[this.wizardStep]
      return pageName ? `批量打标 · ${pageName}` : '批量打标'
    },
    /** 方式页不展示顶部说明轮播（面板内已有引导） */
    showHelpCarousel() {
      return this.wizardStep !== this.wizardSteps.METHOD
    },
    /** 左栏步骤轨：随当前页更新 done / active / pending */
    wizardSidebarSteps() {
      const done = 'done'
      const active = 'active'
      const pending = 'pending'
      if (this.wizardStep === this.wizardSteps.METHOD) {
        return [
          { id: 'scope', label: '选资源', status: done },
          { id: 'method', label: '选方式', status: active },
          { id: 'tag', label: '打标', status: pending },
        ]
      }
      if (this.isTagStep) {
        if (this.excelDirectEntry) {
          return [
            { id: 'scope', label: '选资源', status: done },
            { id: 'method', label: '选方式', status: pending },
            { id: 'tag', label: '打标', status: active },
          ]
        }
        return [
          { id: 'scope', label: '选资源', status: done },
          { id: 'method', label: '选方式', status: done },
          { id: 'tag', label: '打标', status: active },
        ]
      }
      return []
    },
    /** 左栏返回 aria：指向上一级向导页 */
    wizardBackAriaLabel() {
      if (this.wizardStep === this.wizardSteps.METHOD) {
        return '返回选资源'
      }
      if (this.isTagStep && this.excelDirectEntry) {
        return '返回选资源'
      }
      if (this.isTagStep) {
        return '返回选方式'
      }
      return '返回上一步'
    },
    isScopeStep() {
      return this.wizardStep === this.wizardSteps.SCOPE
    },
    /** 选择资源页左侧固定：全部资源 · 共 N 条可选 */
    pickerScopeLabel() {
      return buildPickerScopeLabel({
        hasFilters: this.hasFilters,
        filterSummary: this.filterSummary,
        totalCount: this.pickerListTotalCount,
      })
    },
    helpCarouselSlides() {
      return buildWizardHelpCarouselSlides(this.wizardStep)
    },
    helpCarouselPauseOnHover() {
      return BATCH_TAG_HELP_CAROUSEL.pauseOnHover !== false
    },
    previewEmptyHint() {
      if (this.wizardStep === this.wizardSteps.PAGE_TAG) {
        return '选择 Tag 或切换更新模式后，将在此显示变更 diff'
      }
      return '上传 Excel 并校验通过后，将在此显示变更 diff'
    },
  },
  watch: {
    visible(val) {
      if (val) {
        this.bootstrapDialogState()
      } else {
        /** 退出批量打标：清列表勾选与弹窗内存态；sessionStorage 草稿保留供下次恢复 */
        this.$emit('clear-list-selection')
        this.resetWizardState()
      }
    },
    wizardStep(step) {
      this.previewOverlayOpen = false
      if (
        step === this.wizardSteps.PAGE_TAG ||
        step === this.wizardSteps.EXCEL_TAG
      ) {
        this.$nextTick(() => {
          this.scheduleRefreshPreview()
        })
      } else {
        this.previewRows = []
        this.clearWarnings = []
      }
    },
    pageState: {
      deep: true,
      handler() {
        if (this.wizardStep === this.wizardSteps.PAGE_TAG) {
          this.scheduleRefreshPreview()
        }
      },
    },
    pageSelection() {
      if (this.wizardStep === this.wizardSteps.PAGE_TAG) {
        this.scheduleRefreshPreview()
      }
    },
    excelState: {
      deep: true,
      handler() {
        if (this.wizardStep === this.wizardSteps.EXCEL_TAG) {
          this.scheduleRefreshPreview()
        }
      },
    },
  },
  mounted() {
    document.addEventListener('keydown', this.handlePreviewOverlayKeydown)
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handlePreviewOverlayKeydown)
    if (this.previewRefreshTimer) {
      clearTimeout(this.previewRefreshTimer)
      this.previewRefreshTimer = null
    }
  },
  methods: {
    bootstrapDialogState() {
      this.pageSelection = [...(this.selection || [])]
      this.frozenFilters = { ...(this.filters || {}) }
      this.storedDraft = readBatchTagDraft()
      this.draftBannerDismissed = false
      this.excelDirectEntry = false
      this.wizardStep = BATCH_TAG_WIZARD_STEP.SCOPE
      this.tagMethod = this.defaultTab === 'excel' ? 'excel' : 'page'
      this.resetTagWorkflowState()
    },
    /** 关闭弹窗时归零向导与打标草稿，避免下次打开残留步骤或 Excel 步骤 */
    resetWizardState() {
      this.pageSelection = []
      this.pickerListTotalCount = 0
      this.wizardStep = BATCH_TAG_WIZARD_STEP.SCOPE
      this.tagMethod = 'page'
      this.excelDirectEntry = false
      this.draftBannerDismissed = false
      this.resetTagWorkflowState()
    },
    resetTagWorkflowState() {
      this.pageState = {
        selectedTags: [],
        mode: TAG_BIND_MODE.APPEND,
      }
      this.excelState = {
        mode: '',
        parsedRows: [],
        scopeKeySet: [],
      }
      this.previewRows = []
      this.clearWarnings = []
      this.existingTagMap = {}
      this.previewLoading = false
      this.submitting = false
      this.previewOverlayOpen = false
      if (this.previewRefreshTimer) {
        clearTimeout(this.previewRefreshTimer)
        this.previewRefreshTimer = null
      }
      this.$nextTick(() => {
        if (this.$refs.excelTab && this.$refs.excelTab.resetWizard) {
          this.$refs.excelTab.resetWizard()
        }
      })
    },
    /**
     * 清空打标绑定结果（已选 Tag / Excel 解析行 / scopeKeySet），
     * 但保留用户选的 mode（mode 是用户偏好，不必每次换资源都重新选）。
     * 用于：第 3 步返回第 2 步时，避免旧 Tag 残留在新资源上。
     * Med: 不能直接复用 resetTagWorkflowState，因为那个会把 mode 也清掉，
     * 用户从「页面打标」切回「选方式」再切回「页面打标」会被强制重置 mode，体验差。
     */
    resetTagBindings() {
      this.pageState = {
        ...this.pageState,
        selectedTags: [],
      }
      this.excelState = {
        ...this.excelState,
        parsedRows: [],
        parseErrors: [],
      }
      this.previewRows = []
      this.clearWarnings = []
      this.$nextTick(() => {
        if (this.$refs.excelTab && this.$refs.excelTab.resetWizard) {
          this.$refs.excelTab.resetWizard()
        }
      })
    },
    handleDialogOpened() {
      if (this.wizardStep !== BATCH_TAG_WIZARD_STEP.SCOPE) {
        return
      }
      this.$nextTick(() => {
        this.remeasureResourcePage()
      })
    },
    remeasureResourcePage() {
      const picker = this.$refs.resourcePicker
      if (picker && picker.remeasureTableHeight) {
        picker.remeasureTableHeight()
      }
    },
    handleClose() {
      this.$emit('update:visible', false)
    },
    handleSelectionUpdate(next) {
      this.pageSelection = Array.isArray(next) ? next : []
    },
    handlePickerScopeMetaChange({ totalCount }) {
      this.pickerListTotalCount = Number.isFinite(Number(totalCount))
        ? Number(totalCount)
        : 0
    },
    handleScopeNext() {
      if (!this.pageSelection.length) {
        this.$message.warning('请至少选择一条资源')
        return
      }
      this.wizardStep = BATCH_TAG_WIZARD_STEP.METHOD
    },
    handleMethodSelect(method) {
      this.excelDirectEntry = false
      this.tagMethod = method === 'excel' ? 'excel' : 'page'
      this.wizardStep =
        this.tagMethod === 'excel'
          ? BATCH_TAG_WIZARD_STEP.EXCEL_TAG
          : BATCH_TAG_WIZARD_STEP.PAGE_TAG
    },
    /**
     * 直接进入 Excel 打标：跳过选资源/选方式，落在完整 Excel 页；
     * 更新模式为空，须用户先选模式后才能上传（uploadBlocked）。
     */
    enterDirectExcelUpload() {
      this.excelDirectEntry = true
      this.tagMethod = 'excel'
      this.draftBannerDismissed = true
      this.wizardStep = BATCH_TAG_WIZARD_STEP.EXCEL_TAG
      this.excelState = {
        mode: '',
        parsedRows: [],
        scopeKeySet: [],
      }
      this.previewRows = []
      this.clearWarnings = []
      this.$nextTick(() => {
        if (this.$refs.excelTab && this.$refs.excelTab.resetWizard) {
          this.$refs.excelTab.resetWizard()
        }
        if (this.$refs.excelTab && this.$refs.excelTab.focusModeSection) {
          this.$refs.excelTab.focusModeSection()
        }
      })
    },
    handleRestoreDraft() {
      const draft = readBatchTagDraft()
      if (!draft) {
        this.storedDraft = null
        return
      }
      this.excelDirectEntry = !this.pageSelection.length
      this.tagMethod = 'excel'
      this.wizardStep = BATCH_TAG_WIZARD_STEP.EXCEL_TAG
      this.draftBannerDismissed = true
      this.excelState = {
        mode: draft.mode || '',
        parsedRows: [],
        scopeKeySet: draft.scopeKeySet || [],
      }
      this.previewRows = []
      this.clearWarnings = []
      this.$nextTick(() => {
        if (this.$refs.excelTab && this.$refs.excelTab.restoreFromDraft) {
          this.$refs.excelTab.restoreFromDraft(draft)
        }
        this.scheduleRefreshPreview()
      })
    },
    handleDiscardDraft() {
      clearBatchTagDraft()
      this.storedDraft = null
      this.draftBannerDismissed = true
    },
    handleExcelScopeEstablished(payload = {}) {
      const scopeKeySet = payload.scopeKeySet || []
      if (!scopeKeySet.length) {
        return
      }
      this.excelState = {
        ...this.excelState,
        mode: payload.mode || this.excelState.mode,
        scopeKeySet,
      }
      writeBatchTagDraft({
        scopeKeySet,
        mode: payload.mode || this.excelState.mode,
        exportedMode: payload.exportedMode || payload.mode || this.excelState.mode,
        exportedAt: Date.now(),
        resourceCount: scopeKeySet.length,
        resourceType: this.resourceType,
        source: payload.source || 'export',
      })
      this.storedDraft = readBatchTagDraft()
      this.draftBannerDismissed = true
    },
    openPreviewOverlay() {
      this.previewOverlayOpen = true
      /** 已有预览数据时不重新 loading，避免展开瞬间闪白 */
      if (!this.previewRows.length && !this.previewLoading) {
        this.scheduleRefreshPreview()
      }
    },
    closePreviewOverlay() {
      this.previewOverlayOpen = false
    },
    togglePreviewOverlay() {
      if (this.previewOverlayOpen) {
        this.closePreviewOverlay()
        return
      }
      this.openPreviewOverlay()
    },
    handlePreviewOverlayKeydown(event) {
      if (event.key === 'Escape' && this.previewOverlayOpen) {
        this.closePreviewOverlay()
      }
    },
    handleExcelParsed() {
      this.scheduleRefreshPreview()
    },
    handleWizardBack() {
      if (
        this.wizardStep === BATCH_TAG_WIZARD_STEP.PAGE_TAG ||
        this.wizardStep === BATCH_TAG_WIZARD_STEP.EXCEL_TAG
      ) {
        if (this.wizardStep === BATCH_TAG_WIZARD_STEP.EXCEL_TAG && this.excelDirectEntry) {
          this.excelDirectEntry = false
          // Med: 返回选资源前清空打标工作区,避免换资源后旧 Tag 残留导致错绑。
          // 仅清 selectedTags / parsedRows,保留 mode（用户偏好,不必重新选）。
          this.resetTagBindings()
          this.wizardStep = BATCH_TAG_WIZARD_STEP.SCOPE
          this.$nextTick(() => {
            this.remeasureResourcePage()
          })
          return
        }
        if (this.wizardStep === BATCH_TAG_WIZARD_STEP.EXCEL_TAG) {
          this.excelDirectEntry = false
        }
        // Med: 从打标页返回选方式时,清空已选 Tag / Excel 解析结果,
        // 防止用户换资源后再进打标页把旧 Tag 误绑到新资源。
        this.resetTagBindings()
        this.wizardStep = BATCH_TAG_WIZARD_STEP.METHOD
        return
      }
      if (this.wizardStep === BATCH_TAG_WIZARD_STEP.METHOD) {
        this.wizardStep = BATCH_TAG_WIZARD_STEP.SCOPE
        this.$nextTick(() => {
          this.remeasureResourcePage()
        })
      }
    },
    getActiveContext() {
      if (this.wizardStep === BATCH_TAG_WIZARD_STEP.PAGE_TAG) {
        const tab = this.$refs.pageTab
        if (!tab) {
          return { error: '页面打标加载中', mode: this.pageState.mode, items: [] }
        }
        const error = tab.validate()
        return {
          error,
          mode: this.pageState.mode,
          items: tab.buildItems(),
        }
      }
      if (this.wizardStep === BATCH_TAG_WIZARD_STEP.EXCEL_TAG) {
        const tab = this.$refs.excelTab
        if (!tab) {
          return { error: 'Excel 打标加载中', mode: this.excelState.mode, items: [] }
        }
        const error = tab.validate()
        return {
          error,
          mode: this.excelState.mode,
          items: tab.buildItems(),
        }
      }
      return { error: '请先完成打标配置', mode: TAG_BIND_MODE.APPEND, items: [] }
    },
    getPreviewBuildContext() {
      if (this.wizardStep === BATCH_TAG_WIZARD_STEP.PAGE_TAG) {
        const tab = this.$refs.pageTab
        if (!tab || !this.pageSelection.length) {
          return null
        }
        return {
          mode: this.pageState.mode,
          items: tab.buildItems(),
          selectedTagNames: this.pageState.selectedTags.map((tag) => tag.name),
        }
      }
      if (this.wizardStep === BATCH_TAG_WIZARD_STEP.EXCEL_TAG) {
        const parsedRows = this.excelState.parsedRows || []
        if (!parsedRows.length) {
          return null
        }
        return {
          mode: this.excelState.mode,
          items: parsedRows.map((row) => ({
            resourceKey: row.resourceKey,
            title: row.title,
            resourceType: row.resourceType,
            tagNames: row.tagNames,
          })),
          selectedTagNames: [],
        }
      }
      return null
    },
    scheduleRefreshPreview() {
      if (this.previewRefreshTimer) {
        clearTimeout(this.previewRefreshTimer)
      }
      this.previewRefreshTimer = setTimeout(() => {
        this.previewRefreshTimer = null
        this.refreshPreview()
      }, 280)
    },
    async refreshPreview() {
      if (!this.isTagStep) {
        this.previewRows = []
        this.clearWarnings = []
        return
      }
      const buildContext = this.getPreviewBuildContext()
      if (!buildContext || !buildContext.items.length) {
        this.previewRows = []
        this.clearWarnings = []
        return
      }
      const showBlockingLoader = !this.previewRows.length
      if (showBlockingLoader) {
        this.previewLoading = true
      }
      try {
        const keys = buildContext.items.map((item) => item.resourceKey)
        const tagRes = await resourceTagsByKeys({
          resourceType: this.resourceType,
          resourceKeys: keys,
        })
        if (tagRes.success) {
          this.existingTagMap = tagRes.data || {}
        }
        this.previewRows = buildPreviewRows({
          items: buildContext.items,
          existingTagMap: this.existingTagMap,
          mode: buildContext.mode,
          selectedTagNames: buildContext.selectedTagNames,
        })
        this.clearWarnings = collectClearWarnings(this.previewRows)
      } catch (error) {
        this.previewRows = []
        this.clearWarnings = []
        this.$message.error(error || '预览生成失败')
      } finally {
        if (showBlockingLoader) {
          this.previewLoading = false
        }
      }
    },
    handleConfirmSubmit() {
      const blockReason = this.submitBlockReason
      if (blockReason) {
        this.$message.warning(blockReason)
        return
      }
      const warnings = this.clearWarnings.length
        ? this.clearWarnings
        : this.collectSubmitClearWarnings()
      if (warnings.length) {
        const names = warnings
          .map((row) => row.title || '未命名资源')
          .join('、')
        this.confirmRiskySubmit(
          `覆盖模式下，以下资源将被清空全部 Tag：${names}。是否继续？`
        )
          .then(() => {
            this.submitBatchTag()
          })
          .catch(() => {})
        return
      }
      this.submitBatchTag()
    },
    /**
     * 覆盖清空风险提示：MessageBox 需高于批量打标弹窗，避免用户以为点击无响应。
     */
    confirmRiskySubmit(message, title = '确认清空 Tag') {
      return this.$confirm(message, title, {
        type: 'warning',
        confirmButtonText: '我了解风险，继续提交',
        cancelButtonText: '返回修改',
        customClass: 'link-batch-tag-messagebox',
      })
    },
    /** 预览未刷新时，仍根据当前配置计算将清空的资源 */
    collectSubmitClearWarnings() {
      const context = this.getActiveContext()
      if (context.error || !context.items.length) {
        return []
      }
      if (context.mode !== TAG_BIND_MODE.REPLACE) {
        return []
      }
      const rows = context.items.filter((item) => !(item.tagNames || []).length)
      return rows.map((item) => ({
        title: item.title || item.resourceName || '',
        resourceKey: item.resourceKey,
        willClear: true,
      }))
    },
    submitBatchTag() {
      const context = this.getActiveContext()
      if (context.error) {
        this.$message.warning(context.error)
        return
      }
      this.submitting = true
      batchBindResource({
        resourceType: this.resourceType,
        mode: context.mode,
        items: context.items.map((item) => ({
          resourceKey: item.resourceKey,
          tagNames: item.tagNames,
        })),
      })
        .then((res) => {
          if (!res.success) {
            this.$message.error(res.errMessage || '提交失败')
            return
          }
          const result = res.data || {}
          const failCount = result.failCount || 0
          const successCount = result.successCount || 0
          if (failCount > 0) {
            const detail = (result.failItems || [])
              .slice(0, 3)
              .map((item) => `${item.resourceKey}: ${item.reason}`)
              .join('；')
            this.$message.warning(`完成：成功 ${successCount} 条，失败 ${failCount} 条。${detail}`)
          } else {
            this.$message.success(`打标成功 ${successCount} 条`)
          }
          clearBatchTagDraft()
          this.storedDraft = null
          this.innerVisible = false
          this.$emit('success')
        })
        .catch((error) => {
          this.$message.error(error || '提交失败')
        })
        .finally(() => {
          this.submitting = false
        })
    },
  },
}
</script>

<style>
@import './linkTypography.css';
@import './linkListTheme.css';
@import './linkModuleSurfaces.css';
@import './linkTableHybrid.css';
@import './linkControlBar.css';

/** 向导主按钮：暖灰 emphasis 实底，替代 Element 默认 accent 蓝 */
@import './genericResourceDialogShell.css';

.batch-tag-dialog .batch-tag-dialog__primaryBtn.el-button--primary {
  background-color: var(--link-ink-emphasis) !important;
  border-color: var(--link-ink-emphasis) !important;
  color: #faf8f5 !important;
  font-weight: var(--link-font-semibold);
}

.batch-tag-dialog .batch-tag-dialog__primaryBtn.el-button--primary:hover,
.batch-tag-dialog .batch-tag-dialog__primaryBtn.el-button--primary:focus {
  background-color: var(--link-ink) !important;
  border-color: var(--link-ink) !important;
  color: #faf8f5 !important;
}

.batch-tag-dialog .batch-tag-dialog__primaryBtn.el-button--primary.is-disabled,
.batch-tag-dialog .batch-tag-dialog__primaryBtn.el-button--primary.is-disabled:hover {
  background-color: var(--link-ink-faint) !important;
  border-color: var(--link-ink-faint) !important;
  color: rgba(250, 248, 245, 0.92) !important;
}

.batch-tag-dialog .el-dialog__wrapper {
  overflow: hidden;
}

.batch-tag-dialog .el-dialog__header {
  padding-bottom: 12px;
}

.batch-tag-dialog .el-dialog__body {
  padding: 0 20px 12px;
}

/** 带左栏的向导页：body 左右零 padding，侧栏贴齐弹窗左缘 */
.batch-tag-dialog--method .el-dialog__body,
.batch-tag-dialog--tag .el-dialog__body {
  padding: 0;
}

.batch-tag-dialog.el-dialog {
  --batch-tag-wizard-sidebar-width: 96px;
}

body.el-popup-parent--hidden {
  overflow: hidden !important;
}

/** 方式页无 footer 操作，整块隐藏避免空白条 */
.batch-tag-dialog--method .el-dialog__footer {
  display: none;
}

.batch-tag-dialog__helpDisplay--inWizard {
  flex-shrink: 0;
  margin: 10px 0 10px;
  min-height: var(--link-display-height, 36px);
}

.batch-tag-dialog__draftBanner--inWizard {
  flex-shrink: 0;
  margin: 10px 0 8px;
}

.batch-tag-dialog__title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.batch-tag-dialog__titleText {
  font-size: var(--link-text-xl);
  font-weight: var(--link-font-semibold);
  color: var(--link-ink);
}

.batch-tag-dialog__body {
  flex: 1 1 auto;
  height: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.batch-tag-dialog__draftBanner {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
  padding: 8px 12px;
  border-radius: 8px;
  background: var(--link-surface-head, #f3f0ea);
  border: 1px dashed var(--link-display-border, #e5dfd6);
}

.batch-tag-dialog__draftBannerText {
  font-size: var(--link-text-sm);
  color: var(--link-ink-secondary);
  line-height: 1.45;
}

.batch-tag-dialog__draftBannerActions {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.batch-tag-dialog__helpDisplay {
  flex-shrink: 0;
  margin-bottom: 10px;
  min-height: var(--link-display-height, 36px);
}

/* 弹窗内轮播与列表控制条共用 token，避免 scoped 样式漏改导致 accent 蓝回潮 */
.batch-tag-dialog__helpDisplay .scrollControlBar__broadcastNoteTitle {
  color: var(--link-broadcast-title-ink, var(--link-ink-secondary));
}

.batch-tag-dialog__helpDisplay .scrollControlBar__broadcastNoteSummary {
  color: var(--link-broadcast-body-ink, var(--link-ink-tertiary));
}

/* 选择资源：单条显示屏内左固定范围 + 右轮播，轮播帧不再套第二层边框 */
.batch-tag-dialog__helpBar--scope {
  justify-content: flex-start;
  gap: 8px;
  padding: 0 10px 0 14px;
}

.batch-tag-dialog__helpBar--scope .batch-tag-dialog__helpCarousel {
  flex: 1 1 auto;
  min-width: 0;
}

.batch-tag-dialog__scopeLead {
  flex-shrink: 0;
  font-weight: var(--link-font-semibold);
  color: var(--link-ink-secondary);
  white-space: nowrap;
}

.batch-tag-dialog__helpBar--scope .scrollControlBar__broadcast {
  min-height: 0;
  width: 100%;
}

.batch-tag-dialog__helpBar--scope .scrollControlBar__broadcastNote {
  justify-content: flex-start;
  width: 100%;
  height: auto;
  min-height: 0;
  padding: 0;
  border: none;
  background: transparent;
  border-radius: 0;
}

.batch-tag-dialog__resourcePage {
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
  overflow: hidden;
}

/** 方式页 / 打标页：左栏全高侧栏 + 右侧独立内容区 */
.batch-tag-dialog__wizardStage {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  width: 100%;
  overflow: hidden;
}

.batch-tag-dialog__wizardSidebar {
  flex: 0 0 var(--batch-tag-wizard-sidebar-width, 96px);
  width: var(--batch-tag-wizard-sidebar-width, 96px);
  align-self: stretch;
  display: flex;
  flex-direction: column;
  min-height: 100%;
  background: var(--link-surface-sidebar, rgba(243, 240, 234, 0.72));
  border-right: 1px solid var(--link-display-border, rgba(229, 223, 214, 0.85));
}

.batch-tag-dialog__wizardMain {
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0 24px;
  box-sizing: border-box;
}

.batch-tag-dialog__methodPage {
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  height: 100%;
  overflow: hidden;
}

.batch-tag-dialog__tagWorkspace {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}

.batch-tag-dialog__tagConfig {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* 预览入口在 footer 左侧；主按钮固定在右侧导航区 */
.batch-tag-dialog__footer {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.batch-tag-dialog__footerLeading {
  display: flex;
  align-items: center;
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
}

.batch-tag-dialog__footerNav {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-shrink: 0;
}

/** 更新模式单选：选中态用暖灰 emphasis，不用 Element 默认蓝 */
.batch-tag-dialog .el-radio__input.is-checked .el-radio__inner {
  border-color: var(--link-ink-emphasis) !important;
  background: var(--link-ink-emphasis) !important;
}

.batch-tag-dialog .el-radio__input.is-checked + .el-radio__label {
  color: var(--link-ink-emphasis) !important;
  font-weight: var(--link-font-semibold);
}

.batch-tag-dialog .el-radio__inner:hover {
  border-color: var(--link-ink-muted);
}

.batch-tag-dialog .el-radio__input.is-focus .el-radio__inner {
  border-color: var(--link-ink-emphasis);
}

.batch-tag-dialog__footerSpacer {
  flex: 1 1 auto;
  min-width: 8px;
}

.batch-tag-dialog__previewDockBtn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  padding: 6px 12px;
  border: 1px solid var(--link-line-strong, #e5dfd6);
  border-radius: 999px;
  background: var(--link-surface-muted, #f7f4ef);
  color: var(--link-ink-emphasis);
  font-size: var(--link-text-sm);
  line-height: 1.2;
  white-space: nowrap;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
  overflow: hidden;
}

.batch-tag-dialog__previewDockLabel {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  font-weight: var(--link-font-semibold);
}

.batch-tag-dialog__previewDockMeta {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--link-ink-tertiary);
}

.batch-tag-dialog__previewDockWarn {
  flex-shrink: 0;
  color: var(--link-status-danger-ink);
  font-weight: var(--link-font-semibold);
}

.batch-tag-dialog__previewDockAction {
  flex-shrink: 0;
  padding-left: 4px;
  color: var(--link-ink-emphasis);
  font-weight: var(--link-font-semibold);
  border-left: 1px solid var(--link-line-soft, #ece6de);
}

.batch-tag-dialog__previewDockBtn:hover {
  border-color: var(--link-ink-faint);
  background: var(--link-surface-head, #f3f0ea);
}

/* 预览 bottom sheet：自底部滑出 */
.batch-tag-dialog__previewLayer {
  position: absolute;
  inset: 0;
  z-index: 20;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  pointer-events: none;
}

.batch-tag-dialog__previewBackdrop {
  position: absolute;
  inset: 0;
  background: rgba(28, 25, 23, 0.28);
  pointer-events: auto;
}

.batch-tag-dialog__previewPanel {
  position: relative;
  z-index: 1;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: min(460px, 62%);
  min-height: 220px;
  overflow: hidden;
  background: var(--link-surface, #faf8f5);
  border: 1px solid var(--link-line-strong, #e5dfd6);
  border-bottom: none;
  border-radius: 12px 12px 0 0;
  box-shadow: 0 -8px 28px rgba(28, 25, 23, 0.14);
  pointer-events: auto;
}

.batch-tag-preview-sheet-enter-active,
.batch-tag-preview-sheet-leave-active {
  pointer-events: auto;
}

.batch-tag-preview-sheet-enter-active .batch-tag-dialog__previewBackdrop,
.batch-tag-preview-sheet-leave-active .batch-tag-dialog__previewBackdrop {
  transition: opacity 0.22s ease;
}

.batch-tag-preview-sheet-enter-active .batch-tag-dialog__previewPanel,
.batch-tag-preview-sheet-leave-active .batch-tag-dialog__previewPanel {
  transition: transform 0.28s cubic-bezier(0.32, 0.72, 0, 1);
}

.batch-tag-preview-sheet-enter .batch-tag-dialog__previewBackdrop,
.batch-tag-preview-sheet-leave-to .batch-tag-dialog__previewBackdrop {
  opacity: 0;
}

.batch-tag-preview-sheet-enter .batch-tag-dialog__previewPanel,
.batch-tag-preview-sheet-leave-to .batch-tag-dialog__previewPanel {
  transform: translateY(100%);
}

.batch-tag-preview-sheet-enter-to .batch-tag-dialog__previewPanel,
.batch-tag-preview-sheet-leave .batch-tag-dialog__previewPanel {
  transform: translateY(0);
}

.batch-tag-dialog__previewPanelHead {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px 8px;
  border-bottom: 1px solid var(--link-line-strong, #e5dfd6);
}

.batch-tag-dialog__previewPanelTitle {
  font-size: var(--link-text-md);
  font-weight: var(--link-font-semibold);
  color: var(--link-ink);
}

.batch-tag-dialog__previewPanelMeta {
  font-weight: var(--link-font-regular);
  color: var(--link-ink-tertiary);
}

.batch-tag-dialog__previewCloseBtn {
  padding: 4px 12px;
  border: 1px solid var(--link-line-strong, #e5dfd6);
  border-radius: 6px;
  background: var(--link-surface-muted, #f7f4ef);
  color: var(--link-ink-secondary);
  font-size: var(--link-text-sm);
  cursor: pointer;
}

.batch-tag-dialog__previewCloseBtn:hover {
  background: var(--link-surface-row-hover, #f7f4ef);
  border-color: var(--link-ink-faint);
}

.batch-tag-dialog__previewPanelBody {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}
</style>
